import { Encoding } from '@mode-vegas/vegas';
import * as angular from 'angular';
import { Observable, combineLatest as staticCombineLatest } from 'rxjs';
import { combineLatest, first, map, pluck, switchMap, withLatestFrom } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { SelectedChartService } from '@mode/charts/contract';
import { RenderDataModel } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';
import { MixableBaseController } from '../../../../common/component';
import { getChartDataErrorMsg } from '../../../../lib/charts/chart-data';
import { FeatureToggleService } from '../../../../services/feature-toggle-service';
import { FlamingoService } from '../../../../services/flamingo-service';
import { ChartObservables, DesignerErrorType } from '../../../../types/viz-designer.types';

interface LastRenderedState {
  canRender: boolean;
  isCompleteEncoding: boolean;
  isValid: boolean;
}

class BigValueDesignerRendererController extends MixableBaseController {
  static $inject = ['$scope', 'RxStoreService', 'AnalyticsService', 'FlamingoService', 'FeatureToggleService'];

  token$: Observable<string>;
  readOnly: boolean;
  private objectContext: ChartTypes.ObjectType;
  observables: ChartObservables;
  isViewPage = false;
  private content$: Observable<FlamingoTypes.RecordsResult['content']>;
  private columns$: Observable<FlamingoTypes.RecordsResult['columns']>;
  private activeTableColumn$: Observable<string>;

  private encoding$: Observable<Encoding>;
  private format$: Observable<ChartTypes.ChartFormat>;
  private fieldFormats$: Observable<ChartTypes.FieldFormatMap>;

  private renderModel$: Observable<RenderDataModel>;
  private paging$: Observable<FlamingoTypes.PagingResult>;
  private isValid$: Observable<boolean>;
  private isValidChart$: Observable<boolean>;
  private isCompleteEncoding$: Observable<boolean>;
  private canRender$: Observable<boolean>;
  private vizTitle$: Observable<string>;
  private description$: Observable<string>;
  private runFailed$: Observable<boolean>;
  private showChartLoading$: Observable<boolean>;
  private showChartRenderer$: Observable<boolean>;
  private isAutoUpdateDisabled$: Observable<boolean>;
  private manuallyApplyChanges$: Observable<boolean>;
  private unappliedChangeCount$: Observable<number>;
  private isAutoOrManualRenderEnabled$: Observable<boolean>;
  private isCurrentOrLastRenderable$: Observable<boolean>;
  private isCurrentOrLastRenderedValid$: Observable<boolean>;
  private isCurrentOrLastRenderedComplete$: Observable<boolean>;
  private hasData$: Observable<boolean>;

  private lastRenderedState: LastRenderedState | undefined;

  private chartService: SelectedChartService;

  renderError: string;
  renderErrorHelp: string;

  constructor(
    private $scope: angular.IScope,
    private rxStore: AppStore,
    private analyticsService: AnalyticsService,
    flamingoService: FlamingoService, // for side-effects
    featureService: FeatureToggleService
  ) {
    super();
  }

  $onInit() {
    this.token$ = this.observables.token$;
    this.isValid$ = this.observables.isValid$;
    const planId$ = this.observables.planId$;
    this.runFailed$ = this.observables.runFailed$;
    const chartState$ = this.observables.chartState$;

    this.isValidChart$ = planId$.pipe(
      switchMap(({ token, planId }) =>
        this.rxStore.select(fromFlamingoSelectors.createReadySelector(planId)).pipe(withLatestFrom(this.isValid$))
      ),
      map(([ready, isValid]) => ready && isValid)
    );

    this.isCompleteEncoding$ = this.observables.isComplete$;
    this.canRender$ = this.observables.canRender$;

    // Chart Stuff
    this.format$ = this.observables.format$;
    this.fieldFormats$ = this.observables.fieldFormats$;
    this.vizTitle$ = this.observables.title$;
    this.description$ = this.format$.pipe(pluck('bigValue', 'description'));
    this.encoding$ = this.observables.encoding$;
    this.renderModel$ = this.observables.renderModel$;
    this.isAutoUpdateDisabled$ = this.observables.isAutoUpdateDisabled$;
    this.manuallyApplyChanges$ = this.observables.manuallyApplyChanges$;
    this.unappliedChangeCount$ = this.observables.unappliedChangesCount$;

    // This observable represents the current render state. It is true if auto-update is ON or if the user
    // just applied changes manually.
    this.isAutoOrManualRenderEnabled$ = this.observables.isAutoOrManualRender$;

    // Handles the canRender state either using the current state or the last rendered state.
    this.isCurrentOrLastRenderable$ = staticCombineLatest([this.canRender$, this.isAutoOrManualRenderEnabled$]).pipe(
      map(([canRender, isAutoOrManualRenderEnabled]) =>
        !isAutoOrManualRenderEnabled ? (this.lastRenderedState?.canRender ?? false) : canRender
      )
    );
    // Handles the isValid state either using the current state or the last rendered state.
    this.isCurrentOrLastRenderedValid$ = staticCombineLatest([this.isValid$, this.isAutoOrManualRenderEnabled$]).pipe(
      map(([isValid, isAutoOrManualRenderEnabled]) =>
        !isAutoOrManualRenderEnabled ? this.lastRenderedState.isValid : isValid
      )
    );
    // Handles the isCompleteEncoding state either using the current state or the last rendered state.
    this.isCurrentOrLastRenderedComplete$ = staticCombineLatest([
      this.isCompleteEncoding$,
      this.isAutoOrManualRenderEnabled$,
    ]).pipe(
      map(([isCompleteEncoding, isAutoOrManualRenderEnabled]) =>
        !isAutoOrManualRenderEnabled ? this.lastRenderedState.isCompleteEncoding : isCompleteEncoding
      )
    );

    this.hasData$ = this.renderModel$.pipe(map((renderModel) => (renderModel?.getSeriesData('*')?.length ?? 0) > 0));

    this.showChartLoading$ = staticCombineLatest(
      this.isCompleteEncoding$,
      this.observables.isAutoOrManualRender$,
      this.isValid$,
      this.observables.planLoading$,
      this.observables.planSucceeded$,
      this.observables.planFailed$
    ).pipe(
      map(
        ([isComplete, isAutoOrManualRender, isValid, planLoading, planSucceeded, planFailed]) =>
          isComplete && isAutoOrManualRender && isValid && (planLoading || (!planSucceeded && !planFailed))
      )
    );

    this.showChartRenderer$ = this.isValidChart$.pipe(
      combineLatest(this.canRender$, this.runFailed$, this.isAutoUpdateDisabled$),
      map(([isValid, canRender, runFailed, isAutoUpdateDisabled]) => {
        //
        // This seems a little counter-intuitive that we want to show the chart renderer
        // if auto-update is disabled. However, this flag controls the display of the
        // title and description as well as the mode-big-value-renderer. The renderer
        // itself has its own ng-if which is dependent on !isAutoUpdateDisabled. So
        // if auto-update is disabled, we should still see the title and description,
        // but not the big-value renderer.
        //
        return (isValid && canRender && !runFailed) || isAutoUpdateDisabled;
      })
    );

    // When either isAutoUpdateDisabled or manuallyApplyChanges changes, we need to capture
    // the current chart state.
    staticCombineLatest([this.isAutoUpdateDisabled$, this.manuallyApplyChanges$])
      .pipe(withLatestFrom(this.canRender$, this.isCompleteEncoding$, this.isValid$))
      .subscribe(([[isAutoUpdateDisabled, manuallyApplyChanges], canRender, isCompleteEncoding, isValid]) => {
        if (isAutoUpdateDisabled || manuallyApplyChanges) {
          this.lastRenderedState = {
            canRender,
            isCompleteEncoding,
            isValid,
          };
        } else {
          this.lastRenderedState = undefined;
        }
      });
  }

  updateFormat(format: ChartTypes.ChartFormat) {
    this.chartService.updateChartFormat(format);
  }

  updateTitle(title: string, token: string) {
    this.chartService.updateTitle(token, title);
  }

  updateBigValueDescription(description: string, token: string) {
    this.chartService.updateBigValueDescription(token, description);
  }

  manuallyUpdate() {
    this.chartService.manuallyApplyChanges();

    this.token$.pipe(withLatestFrom(this.observables.chartType$), first()).subscribe(([token, chartType]) => {
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: 'Visualization Message',
        action: 'Manual Apply Changes',
        context: 'visualization',
        context_id: token,
        chartType,
      });
    });
  }

  enableAutoUpdates() {
    this.chartService.updateIsAutoUpdateDisabled(false);

    this.token$.pipe(withLatestFrom(this.observables.chartType$), first()).subscribe(([token, chartType]) => {
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: 'Visualization Message',
        action: 'Switch to automatic',
        context: 'visualization',
        context_id: token,
        chartType,
      });
    });
  }

  renderStart() {
    this.renderError = null;
    this.renderErrorHelp = null;
  }

  renderEnd(err: Error) {
    if (err != null) {
      const { renderError, renderErrorHelp } = getChartDataErrorMsg(err);
      this.renderError = renderError;
      this.renderErrorHelp = renderErrorHelp;
    }
  }
}

export const BigValueDesignerRendererComponent: angular.IComponentOptions = {
  bindings: {
    observables: '<',
    chartService: '<',
    readOnly: '<',
    isViewPage: '<?',
    objectContext: '<',
  },
  template: `
    <div class="editor-chart mode-object editor-multi big-number" ng-if="ctrl.showChartRenderer$ | async:this">
      <div class="chart-wrapper" ng-class="{'no-border': ctrl.isEmptyStateInDesigner() }">
        <div class="chart-content">
          <!-- Big Number Chart Begins -->
          <div class="chart-big-number">
            <div class="chart-title clearfix">
              <in-place-edit
                text="ctrl.vizTitle$ | async:this"
                alt-text="Click to add title"
                on-change="ctrl.updateTitle($text, ctrl.token$ | async:this)"
                no-edit="ctrl.readOnly">
              </in-place-edit>
            </div>
            <div class="chart-title chart-description clearfix">
              <in-place-edit
                text="ctrl.description$ | async:this"
                alt-text="Click to add a description"
                on-change="ctrl.updateBigValueDescription($text, ctrl.token$ | async:this)"
                no-edit="ctrl.readOnly"></in-place-edit>
            </div>
            <mode-big-value-renderer
              ng-if="(ctrl.isCurrentOrLastRenderedValid$ | async:this) && (ctrl.isCurrentOrLastRenderedComplete$ | async:this)"
              ng-show="!ctrl.renderError"
              [token]="ctrl.token$ | async:this"
              [render-model]="ctrl.renderModel$ | async:this"
              [render-context]="'designer'"
              [encoding]="ctrl.encoding$ | async:this"
              [format]="ctrl.format$ | async:this"
              [field-formats]="ctrl.fieldFormats$ | async:this"
              (render-start)="ctrl.renderStart()"
              (render-end)="ctrl.renderEnd($err)"
              [max-font-size]="ctrl.maxFontSize$ | async:this"
              [suspend-rendering]="(ctrl.unappliedChangeCount$ | async:this) > 0">
            </mode-big-value-renderer>
          </div>
        </div>
      </div>
    </div>
    <mode-viz-loader class="viz-loader" ng-show="(ctrl.showChartLoading$ | async:this)"></mode-viz-loader>
    <empty-chart-state
      ng-if="!(ctrl.isCurrentOrLastRenderable$ | async:this) && !ctrl.isViewPage"
      chart-type="'bigValue'"
      is-valid="ctrl.isCurrentOrLastRenderedValid$ | async:this"
      is-complete="ctrl.isCurrentOrLastRenderedComplete$ | async:this">
    </empty-chart-state>
    <viewer-empty-chart
      ng-if="!(ctrl.isCurrentOrLastRenderable$ | async:this) && ctrl.isViewPage"
      chart-type="'bigValue'"
      is-valid="ctrl.isCurrentOrLastRenderedValid$ | async:this"
      is-complete="ctrl.isCurrentOrLastRenderedComplete$ | async:this"
      report-edit-link="(ctrl.observables.links$ | async:this)['web_edit'].href">
    </viewer-empty-chart>
    <designer-error
      error="${DesignerErrorType.RunFailed}"
      ng-if="(ctrl.runFailed$ | async:this) && (ctrl.canRender$ | async:this) && !(ctrl.isAutoUpdateDisabled$ | async:this)"
      on-action="ctrl.selectQuery()"
      object-context="ctrl.objectContext"
      chart-type="ctrl.chartType$ | async:this"
    ></designer-error>
    <div ng-if="ctrl.renderError" class="chart-error-container">
      <div class="error-message error-empty-message">
        <div class="message">
          <h2 class="heading" ng-bind="ctrl.renderError" />
          <p ng-bind="ctrl.renderErrorHelp" />
        </div>
      </div>
    </div>
    <mode-auto-update-message
      ng-if="(ctrl.unappliedChangeCount$ | async:this) > 0 ||
            (!(ctrl.isAutoOrManualRenderEnabled$ | async:this) && !(ctrl.hasData$ | async:this))"
      [is-rendered]="(ctrl.hasData$ | async:this)"
      (update-visualization)="ctrl.manuallyUpdate()"
      (set-to-automatic)="ctrl.enableAutoUpdates()">
    </mode-auto-update-message>
  </div>
  `,
  controller: BigValueDesignerRendererController,
  controllerAs: 'ctrl',
};
